<template>
  <div>
    <vue-good-table
      :columns="learningType === 'social_learning' ? socialLearningColumns : experientialLearningColumns"
      :rows="rows"
      :total-rows="total"
      :sort-options="{ enabled: true }"
      :pagination-options="{ enabled: true }"
      style-class="vgt-table border-0 table-auto"
      @on-sort-change="$emit('sort-change', $event)"
      @on-page-change="$emit('page-change', $event)"
      @on-per-page-change="$emit('per-page-change', $event)"
      styleClass="vgt-table condensed"
    >
      <div slot="emptystate" class="text-center font-size-14">No data available</div>

      <template slot="table-row" slot-scope="props">
        <span v-if="props.column.field === 'action'" class="d-flex gap-3 justify-content-center align-items-center">
          <b-button class="no-wrap btn-pill" variant="info" size="sm" title="View" @click="$emit('view-detail', props.row)">
            <span class="fe fe-eye"></span>
          </b-button>
        </span>

        <span v-else-if="props.column.field === 'status'">
          <div class="d-flex flex-row align-items-center justify-content-start" @click="$emit('change-status', props.row)">
            <span :class="getTrainingStatusClass(props.row.status)">
              {{ snakeToTitle(props.row.status) }}
            </span>
          </div>
        </span>

        <div
          v-else-if="props.column.field === 'level'"
          class="d-flex flex-column gap-1 justify-content-start align-items-start"
        >
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-target': n <= props.row?.competency?.proficiency_standard,
                },
              ]"
            ></div>
          </div>
          <div class="rectangle-container">
            <div
              v-for="n in 5"
              :key="'proficiency_standard-' + n"
              :class="[
                'rectangle',
                {
                  'filled-achievement': n <= props.row?.competency?.proficiency,
                },
              ]"
            ></div>
          </div>
        </div>
        <span v-else-if="props.column.field === 'gap'" class="d-flex gap-3 justify-content-start align-items-center">
          <span :class="getGapBadgeClass(props.row)">
            {{ formatCompetencyGap(props.row) }}
          </span>
        </span>

        <span v-else-if="props.column.field === 'project.duration'">
          <div class="d-flex flex-row align-items-center justify-content-start">
            <span>{{ props.row?.project?.duration || 0 }} days</span>
          </div>
        </span>

        <span v-else-if="props.column.field === 'project.start_date'">
          <div class="d-flex flex-row align-items-center justify-content-start">
            <span>{{ formatDate(props.row?.project?.start_date) || "-" }}</span>
          </div>
        </span>

        <span v-else-if="props.column.field === 'project.end_date'">
          <div class="d-flex flex-row align-items-center justify-content-start">
            <span>{{ formatDate(props.row.project?.end_date) || "-" }}</span>
          </div>
        </span>

        <span v-else class="d-flex align-items-center justify-content-start">
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <template slot="pagination-bottom" slot-scope="props">
        <div class="d-flex justify-content-between align-items-center mt-3 px-3">
          <div class="text-muted">
            Showing
            <strong>{{ showingFrom }} - {{ showingTo }}</strong>
            of <strong>{{ total }}</strong> items
          </div>

          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="pageSize"
            first-number
            last-number
            align="right"
            size="md"
            @change="$emit('page-change-from-bv', $event)"
          />
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import { props } from "vue2-dropzone";
import { getTrainingStatusClass, snakeToTitle, formatDate } from "../utils/utils";

export default {
  name: "ReportTable",
  props: {
    rows: Array,
    total: Number,
    showingFrom: Number,
    showingTo: Number,
    page: Number,
    pageSize: Number,
    learningType: { type: String, default: "social_learning" },
  },

  data() {
    return {
      experientialLearningColumns: [
        {
          label: "Project",
          field: "project.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Start Date",
          field: "project.start_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "End Date",
          field: "project.end_date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Duration",
          field: "project.duration",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
      socialLearningColumns: [
        {
          label: "Title",
          field: "coaching.title",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Competency",
          field: "competency.name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Level",
          field: "level",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Gap",
          field: "gap",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Coaching Type",
          field: "coaching.coaching_type_name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Coaching / Mentor",
          field: "coaching.coach_name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Status",
          field: "status",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Action",
          field: "action",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold w-action",
          tdClass: "color-secondary text-center",
        },
      ],
    };
  },

  methods: {
    getTrainingStatusClass,
    snakeToTitle,
    formatDate,
    getCompetencyGap(row) {
      const standard = Number(row?.competency?.proficiency_standard);
      const actual = Number(row?.competency?.proficiency);

      if (Number.isNaN(standard) || Number.isNaN(actual)) return null;

      return actual - standard;
    },
    formatCompetencyGap(row) {
      const gap = this.getCompetencyGap(row);

      if (gap === null) return "-";
      if (gap > 0) return `+${gap}`;

      return String(gap);
    },
    getGapBadgeClass(row) {
      const gap = this.getCompetencyGap(row);

      if (gap === null) return "badge bg-light text-muted";
      return gap >= 0 ? "badge bg-success" : "badge bg-danger";
    },
  },
};
</script>

<style scoped>
.w-action {
  width: 140px;
  min-width: 140px;
  max-width: 140px;
}

.vgt-table.table-auto table {
  table-layout: auto !important;
}

:deep(.custom-switch .custom-control-label) {
  display: flex !important;
  align-items: center !important;
  cursor: pointer;
}

:deep(.custom-switch .custom-control-input:not(:checked) ~ .custom-control-label::before) {
  background-color: #6c757d !important;
  border-color: #6c757d !important;
}

:deep(.custom-switch .custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #28a745 !important;
  border-color: #28a745 !important;
}

:deep(.custom-switch .custom-control-input:checked ~ .custom-control-label::after) {
  background-color: #fff !important;
}

:deep(.table-header) {
  font-size: 12px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 12px !important;
}

:deep(.vgt-table) {
  border: 1px solid #e6ebf3 !important;
  border-radius: 8px;
  overflow: hidden;
}

:deep(.vgt-table thead th) {
  background: #f8fafd !important;
  color: #8aa0c7 !important;
  padding: 12px 10px !important;
  text-transform: none;
}

:deep(.vgt-table tbody td) {
  border-color: #edf1f7 !important;
  padding: 10px !important;
  vertical-align: middle !important;
}

:deep(.vgt-table tbody tr:hover) {
  background: #fffafa !important;
}

:deep(.badge) {
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  padding: 4px 8px;
}

:deep(.btn-pill.btn-sm) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
}

:deep(.vgt-left-align) {
  align-content: center !important;
}
</style>
